"use client"

import {Actions, buildUserAbilities, CompanySubjects, GlobalSubjects} from '../auth/abilities';
import {useSession} from 'next-auth/react';
import {ReactNode, useMemo} from "react";
import {upgradeRequiredMessage} from "@/app/auth/limits";
import {toast} from "sonner";
import Link from "next/link";
import {orgBillingPath, profileBillingPath} from "@/app/paths";
import {Button} from "@/components/ui/button";
import {useUser} from "@/hooks/queries/use-users";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {cn} from "@/lib/utils";
import {useShallow} from "zustand/react/shallow";
import {subject} from "@casl/ability";
import {useCompanyId} from "@/hooks/use-company-id";
import {OrganizationRole} from "@/app/auth/organizationRoles";

interface CanGlobalParameters {
  children: ReactNode
  do: Actions
  on: GlobalSubjects
  blurForLimits?: boolean
}

export function useCanGlobal(params: Pick<CanGlobalParameters, "do" | "on">): CanCompanyData | undefined {
  const {data: session} = useSession()
  if (!session?.user) {
    return undefined
  }
  const user = session.user;
  const ability = buildUserAbilities(user).global
  const rule = ability.relevantRuleFor(params.do, params.on)
  return {
    forbidden: !rule || rule.inverted,
    reason: rule?.origin?.reason,
  }
}

export function CanGlobal(params: CanGlobalParameters) {
  const {data: session} = useSession()
  if (!session?.user) {
    return null
  }
  const user = session.user;
  const ability = buildUserAbilities(user).global
  const rule = ability.relevantRuleFor(params.do, params.on)
  if (!rule) {
    return null
  }
  const {inverted, reason} = rule.origin
  return <CanWithLimitsCheck inverted={inverted} reason={reason} blurForLimits={params.blurForLimits}>
    {params.children}
  </CanWithLimitsCheck>
}

export interface CanCompanyData {
  forbidden: boolean
  reason?: string | undefined
}

export function useCanDocumentLike(params: { do: Actions, documentId: string | undefined, subj: string }) {
  const {companyId} = useCompanyId()
  const {documentId, subj: subjectName} = params
  const docProps = useBoundStore(useShallow(s => {
    const doc = documentId && s.documents[documentId]
    if (!doc) {
      return undefined
    }
    const {templateId, isDraftPart} = doc
    return {templateId, isDraftPart}
  }))
  const subj = useMemo(() => docProps
      ? subject(subjectName, {companyId, ...docProps}) : subject(subjectName, {companyId}),
    [subjectName, companyId, docProps])
  const info = useCanOptionalCompany({do: params.do, on: subj, forbidIfUndefined: documentId !== undefined})
  return {info, subj}
}

export function useCanDocument(params: { do: Actions, id: string | undefined }) {
  return useCanDocumentLike({documentId: params.id, do: params.do, subj: 'Document'})
}

export function useCanCompany(params: { do: Actions, on: CompanySubjects }): CanCompanyData | undefined {
  return useCanOptionalCompany(params)
}

export function useCanOptionalCompany(params: {
  do: Actions,
  on: CompanySubjects | undefined,
  forbidIfUndefined?: boolean,
}): CanCompanyData | undefined {
  const {data: session} = useSession()
  useUser()
  if (params.on === undefined) {
    return {forbidden: !!params.forbidIfUndefined}
  }
  if (!session?.user) {
    return undefined
  }
  const user = session.user;
  const ability = buildUserAbilities(user).company
  const rule = ability.relevantRuleFor(params.do, params.on)
  const forbidden = !rule || rule.inverted
  const reason = rule?.origin?.reason
  return {forbidden, reason}
}

export interface CanCompanyParameters {
  children: ReactNode
  do: Actions
  on: CompanySubjects
  blurForLimits?: boolean
  forbidForLimits?: boolean
}

export function CanCompany(params: CanCompanyParameters) {
  const canInfo = useCanCompany(params)
  if (!canInfo) {
    return null
  }
  const {reason, forbidden} = canInfo
  return <CanWithLimitsCheck inverted={forbidden} reason={reason} blurForLimits={params.blurForLimits}
                             forbidForLimits={params.forbidForLimits}
                             companyId={companyIdFromSubject(params.on)}>
    {params.children}
  </CanWithLimitsCheck>
}

function CanWithLimitsCheck(
  {blurForLimits, inverted, reason, forbidForLimits, companyId, children}:
  {
    blurForLimits?: boolean,
    inverted: boolean | undefined,
    reason: string | undefined,
    children: ReactNode
    forbidForLimits?: boolean
    companyId?: number
  }) {
  if (!inverted) {
    // Allowed.
    return <>{children}</>
  }
  if ((blurForLimits || forbidForLimits) && reason?.includes(upgradeRequiredMessage)) {
    return <ForbidDueToUpgrade blur={blurForLimits} companyId={companyId}>{children}</ForbidDueToUpgrade>
  }
  return null
}

export function notifyIfUpgradeRequired(reason: string | undefined, companyId?: number) {
  if (reason?.includes(upgradeRequiredMessage)) {
    notifyUpgradeRequired(companyId)
  }
}

function ForbidDueToUpgrade({blur, companyId, children}: { blur?: boolean, companyId?: number, children: ReactNode }) {
  return <div className={cn(blur && "opacity-50")} onClick={e => {
    e.stopPropagation()
    e.preventDefault()
    notifyUpgradeRequired(companyId)
  }}>
    <div className="pointer-events-none">{children}</div>
  </div>
}

export function notifyUpgradeRequired(companyId?: number) {
  useBoundStore.getState().setForceTrialModal(true)
  const billingLink = getUpgradeToastBillingLink(companyId)
  toast.error(<div className="space-y-2">
    <div>You have hit your plan’s limit. Please upgrade to complete this action. Thanks!</div>
    {billingLink.href ? (
      <div className="flex items-center justify-center"><Link href={billingLink.href}>
        <Button variant="outline" size="sm" className="cursor-pointer">Upgrade</Button>
      </Link></div>
    ) : (
      <div className="text-sm">Reach out to your organization admin to upgrade.</div>
    )}
  </div>)
}

function getUpgradeToastBillingLink(companyId?: number): { href?: string } {
  const state = useBoundStore.getState()
  if (!companyId) {
    return {href: profileBillingPath()}
  }
  const orgId = state.companies[companyId]?.orgId
    ?? state.ownCompanyLimits[companyId]?.orgId
    ?? Object.values(state.organizations).find(org => org.companyIds.includes(companyId))?.id
  if (!orgId) {
    return {href: profileBillingPath()}
  }
  const role = state.ownOrgDetails.find(details => details.orgId === orgId)?.role
  if (role === OrganizationRole.OWNER) {
    return {href: orgBillingPath(orgId)}
  }
  return {}
}

function companyIdFromSubject(subj: CompanySubjects): number | undefined {
  if (typeof subj !== "object" || subj === null) {
    return undefined
  }
  return "companyId" in subj && typeof subj.companyId === "number" ? subj.companyId : undefined
}

export function notifyForbidden() {
  toast.error(<div className="space-y-2">
    <div>The operation is forbidden</div>
  </div>)
}
